// Agent LEXA - global floating support widget (docs/DECISIONS.md,
// 2026-08-28 "LEXA - Phase 3"; brief Section 3.1: "A global floating
// button - Agent LEXA - available on every page"). Mounted once in
// StudentShell/StudentLayout.jsx so it's present on every authenticated
// student page. Escalation is a deliberate, explicit action (a "Create a
// support request" button + a short issue-summary prompt), never inferred
// from chat text - matches this codebase's general preference for
// explicit state-changing actions over parsed intent.

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircleQuestion, Minus, Send } from 'lucide-react';
import { useThemeMode } from '../../context/ThemeContext';
import { agentLexaSupport, escalateLexaSupportTicket } from '../../services/lexa';
import MathText from '../MathText';

export default function AgentLexaWidget() {
  const location = useLocation();
  const { isDarkTheme } = useThemeMode();
  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [showEscalateForm, setShowEscalateForm] = useState(false);
  const [issueSummary, setIssueSummary] = useState('');
  const [isEscalating, setIsEscalating] = useState(false);
  const [escalationTicketId, setEscalationTicketId] = useState(null);

  const handleSend = async () => {
    const message = input.trim();
    if (!message || isSending) return;

    setMessages((prev) => [...prev, { role: 'user', content: message }]);
    setInput('');
    setIsSending(true);
    setError(null);

    try {
      const result = await agentLexaSupport({ conversationId, message, page: location.pathname });
      setConversationId(result.conversationId);
      setMessages((prev) => [...prev, { role: 'assistant', content: result.response }]);
    } catch (err) {
      setError(err.message || 'Agent LEXA is unavailable right now.');
    } finally {
      setIsSending(false);
    }
  };

  const handleEscalate = async () => {
    if (!conversationId || !issueSummary.trim() || isEscalating) return;
    setIsEscalating(true);
    setError(null);

    try {
      const result = await escalateLexaSupportTicket({
        conversationId, issueSummary: issueSummary.trim(),
        browserInfo: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      });
      setEscalationTicketId(result.ticketId);
      setShowEscalateForm(false);
    } catch (err) {
      setError(err.message || 'Failed to create a support request.');
    } finally {
      setIsEscalating(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label="Open Agent LEXA"
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-brand-gradient text-white px-4 py-3 shadow-lg hover:opacity-90 transition-opacity"
      >
        <MessageCircleQuestion size={20} />
        <span className="text-sm font-semibold">Agent LEXA</span>
      </button>
    );
  }

  const bodyBg = isDarkTheme ? 'bg-navy border-white/15' : 'bg-white border-gray-200';
  const inputBg = isDarkTheme
    ? 'bg-white/5 border-white/10 text-white'
    : 'bg-gray-50 border-gray-300 text-gray-800';
  const mutedText = isDarkTheme ? 'text-white/60' : 'text-gray-500';
  const bubbleBg = isDarkTheme ? 'bg-white/10 text-white/90' : 'bg-gray-100 text-gray-800';
  const borderT = isDarkTheme ? 'border-white/10' : 'border-gray-200';

  return (
    <div className={`fixed bottom-5 right-5 z-50 flex flex-col w-96 h-[34rem] max-h-[85vh] rounded-card border shadow-2xl overflow-hidden ${bodyBg}`}>
      <div className="flex items-center justify-between px-3 py-2 bg-brand-gradient text-white flex-shrink-0">
        <span className="text-sm font-semibold">Agent LEXA</span>
        {/* Minimize, not close/discard - the conversation state (messages,
            conversationId) is preserved while collapsed since this
            component stays mounted in StudentLayout.jsx the whole time;
            direct user feedback asked for a minimize affordance here. */}
        <button type="button" onClick={() => setIsOpen(false)} aria-label="Minimize Agent LEXA">
          <Minus size={16} />
        </button>
      </div>

      <div className="flex-1 flex flex-col gap-2 p-3 overflow-y-auto text-sm">
        {messages.length === 0 && (
          <div className={mutedText}>How may I assist you?</div>
        )}
        {messages.map((message, index) => (
          <div key={index} className={message.role === 'user' ? 'text-right' : 'text-left'}>
            <span className={`inline-block px-2 py-1 rounded ${bubbleBg}`}>
              <MathText text={message.content} />
            </span>
          </div>
        ))}
        {error && <div className="text-xs text-red-400">{error}</div>}
        {escalationTicketId && (
          <div className={`text-xs ${isDarkTheme ? 'text-emerald-300' : 'text-emerald-700'}`}>
            Support request created (ID: {escalationTicketId}). Our team will follow up.
          </div>
        )}
      </div>

      {showEscalateForm ? (
        <div className={`flex flex-col gap-2 p-3 border-t flex-shrink-0 ${borderT}`}>
          <input
            type="text"
            value={issueSummary}
            onChange={(event) => setIssueSummary(event.target.value)}
            placeholder="Briefly describe the issue"
            className={`px-2 py-1 rounded border text-sm ${inputBg}`}
            autoFocus
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleEscalate}
              disabled={isEscalating || !issueSummary.trim()}
              className="flex-1 px-3 py-1.5 rounded-btn text-xs font-semibold bg-brand-blue text-white hover:bg-brand-blue-hover disabled:opacity-50"
            >
              {isEscalating ? 'Creating...' : 'Create support request'}
            </button>
            <button
              type="button"
              onClick={() => setShowEscalateForm(false)}
              className={`px-3 py-1.5 rounded-btn text-xs font-medium ${
                isDarkTheme ? 'bg-white/10 text-white/80 hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className={`flex flex-col gap-2 p-3 border-t flex-shrink-0 ${borderT}`}>
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && handleSend()}
              disabled={isSending}
              placeholder="Type your message..."
              className={`flex-1 px-2 py-1 rounded border text-sm ${inputBg}`}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={isSending || !input.trim()}
              aria-label="Send"
              className="px-3 py-1 rounded bg-brand-blue text-white hover:bg-brand-blue-hover disabled:opacity-50"
            >
              <Send size={14} />
            </button>
          </div>
          {conversationId && !escalationTicketId && (
            <button
              type="button"
              onClick={() => setShowEscalateForm(true)}
              className={`text-xs text-left hover:underline ${mutedText}`}
            >
              Couldn't resolve this? Create a support request.
            </button>
          )}
        </div>
      )}
    </div>
  );
}
